import { ReactElement, ReactNode, useState, useEffect, useRef } from 'react'
import cn from 'classnames'
import IconInputArrow from 'images/icon-input-arrow.svg'
import IconBigInputArrow from 'images/icon-input-big-arrow.svg'
import IconLoading from 'images/icon-loader-blue.svg'

import onlyNumberChanger from 'utils/only-numbers-value'
import { formatNumber } from 'utils/formatters'
import { adjustCursorPosition } from 'utils/input-cursor'

import Input from 'components/app/input'
import { NetworkLabel } from 'components/ui/network-label'

import { ICurrency, ICurrencyWithBalance } from 'store/fetchers/currencies'

import { ReactSVG } from 'react-svg'

import { useTranslation } from 'utils/use-translation'

import { floorNumber } from 'utils/floor-number'

import { countDecimalPlaces } from 'utils/count-decimal-places'

import { CoinList } from '../coins-dropdown'

import Notice from '../../../notice'

import IconArrow from './chevron-down.svg'

import styles from './styles.module.scss'
import useViewport from 'libs/use-viewport'

export default function InputCalculator({
  onChangeAmount,
  onChangeCoin,
  value,
  label,
  coin,
  list = [],
  children,
  isLoading,
  onEnter,
  isStableFirst = false,
  className = null,
  labelClassName = null,
  inputClassName = null,
  inputWrapperClassName = null,
  bigCurBlock = false,
  showBalance,
  listFullWidth = false,
  isExchangePage = false,
  isExchangeLayout = false,
  isVip = false,
  isNoticeShow = false,
  isRepaymentPage = false,
  inputMode = 'text',
  type = 'primary',
  showBalanceType = 'primary',
  noticeClassName,
  showList = true,
}: {
  onChangeAmount: (value: string) => void
  onChangeCoin: (coin: ICurrency) => void
  value: string
  label: string
  coin: ICurrencyWithBalance
  list: ICurrencyWithBalance[]
  children: ReactNode
  isLoading: boolean
  onEnter?: () => void
  isStableFirst?: boolean
  className?: string
  labelClassName?: string
  inputClassName?: string
  inputWrapperClassName?: string
  bigCurBlock?: boolean
  showBalance?: boolean
  listFullWidth?: boolean
  isExchangePage?: boolean
  isExchangeLayout?: boolean
  isVip?: boolean
  balanceInUsd?: number
  isNoticeShow?: boolean
  isRepaymentPage?: boolean
  type?: 'primary' | 'secondary'
  showBalanceType?: 'primary' | 'secondary'
  noticeClassName?: string
  inputMode?: 'text' | 'email' | 'search' | 'none' | 'tel' | 'url' | 'numeric' | 'decimal'
  showList?: boolean
}): ReactElement {
  const { t } = useTranslation()
  const [veiw] = useViewport()
  const isMobile = ['xs-mobile', 'mobile'].includes(veiw)

  const [isVisibleList, setVisibleList] = useState(false)
  const isError = false
  const formattedLabel = label.replace(' ', '_').toLowerCase()
  const [formattedValue, setFormattedValue] = useState(value)
  const [prevValue, setPrevValue] = useState<string>(value ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  const coinListRef = useRef(null)
  const ref = useRef(null)

  useEffect(() => {
    if (value !== null && value !== undefined) {
      const strVavue = value.toString()
      setFormattedValue(formatNumber(strVavue.replace(/\s/g, '')))
      setPrevValue(formatNumber(strVavue.replace(/\s/g, '')))
    }
  }, [value])

  const handleChangeAmount = (event) => {
    if (coin) {
      let rawValue: string = event.target.value.replace(/\s/g, '')

      if (rawValue[0] === '0' && prevValue.length === 0) {
        rawValue = '0.' + rawValue.slice(2, rawValue.length)
      }
      if (rawValue[0] === '0' && prevValue.length !== 0 && rawValue.length === 1) {
        rawValue = ''
      }
      setPrevValue(rawValue)

      const parts = rawValue.split('.')
      if (parts[1]?.length > coin.decimal_places) {
        return
      }

      let cursorPosition = event.target.selectionStart
      let newValue = ''

      if (event.nativeEvent.inputType === 'deleteContentBackward') {
        cursorPosition += 1
      } else if (event.nativeEvent.inputType === 'deleteContentForward') {
        cursorPosition += 1
      } else {
        cursorPosition -= 1
      }

      onlyNumberChanger(rawValue, (newRawValue) => {
        newValue = newRawValue

        const newFormattedValue = formatNumber(newValue)

        setTimeout(() => {
          if (inputRef.current) {
            const newCursorPosition = cursorPosition - (formattedValue.length - newFormattedValue.length)
            inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
          }
        }, 0)
      })
      onlyNumberChanger(rawValue, onChangeAmount)
    }
  }

  const handleCoinListClose = () => {
    setVisibleList(false)
  }

  const handleChooseCoin = (coin) => {
    onChangeCoin(coin)
    handleCoinListClose()
  }

  const handleOpenList = () => {
    setVisibleList((st) => {
      const newState = !st
      if (newState && coinListRef.current) {
        coinListRef.current.resetSearch()
      }
      return newState
    })
  }

  return (
    <div className={cn(styles.changeCoinFrom, className, { [styles.exchangePage]: isExchangePage })}>
      <label
        className={cn(
          styles.name,
          labelClassName,
          { [styles.exchangeLayout]: isExchangeLayout },
          { [styles.repay]: isRepaymentPage },
        )}
        htmlFor={formattedLabel}
      >
        {label}{' '}
        {isNoticeShow && (
          <Notice leftPos="-50px" alt="repayment amount notice" wrapperClassName={cn(styles.notice, noticeClassName)}>
            {t('does_not_include_fees')}
          </Notice>
        )}
      </label>
      <div
        className={cn(
          styles.inputBox,
          inputWrapperClassName,
          { [styles.inputBox_exchangeLayout]: isExchangeLayout },
          { [styles.inputBox_repay]: isRepaymentPage },
          { [styles.inputBoxOpenedFull]: isVisibleList && listFullWidth },
          { [styles.inputBoxOpened]: isVisibleList && !listFullWidth },
          { [styles.vip]: isVip },
          { [styles.inputErrorValue]: isError },
        )}
      >
        <Input
          ref={inputRef}
          className={cn(
            styles.inputField,
            inputClassName,
            { [styles.inputFieldOpened]: isVisibleList },
            { [styles.exchangeLayout]: isExchangeLayout },
            { [styles.inputField_repay]: isRepaymentPage },
            { [styles.secondary__input]: type === 'secondary' },
          )}
          onEnter={onEnter}
          value={isLoading ? '' : formattedValue}
          onChange={handleChangeAmount}
          onKeyDown={adjustCursorPosition}
          onMouseUp={adjustCursorPosition}
          id={formattedLabel}
          inputMode={inputMode}
        />
        {isLoading && <img src={IconLoading} className={styles.loader} alt="loading" />}
        {children}
        <div
          ref={ref}
          className={cn(styles.openListButton,
            { [styles.openListButton_repay]: isRepaymentPage },
            { [styles.secondary__code]: type === 'secondary' },
            { [styles['show-balance__secondary']]: showBalanceType === 'secondary' },
          )}
          onClick={() => {
            if (showList) handleOpenList()
          }}
        >
          {coin && coin.code && (
            showBalanceType === 'secondary'
              ? <div className={styles.coin}>
                <img className={styles.coinImage} alt={coin.code} src={coin.logo_url} />
                <div className={styles.mainData}>
                  <div className={styles.coinMainData}>
                    <span className={styles.CoinCode}>{coin.code}</span>
                    <NetworkLabel className={styles.input__network} currency={coin} />
                  </div>
                  {!isMobile && showBalance && <span className={styles.coinBalance}>
                    {t('balance')}: {floorNumber(coin?.balance ?? 0, 6)}
                  </span>}
                </div>
              </div> :
              <div
                className={cn(
                  styles.currency,
                  { [styles.currency_bigMode]: bigCurBlock },
                  { [styles.exchangePage]: isExchangePage },
                  { [styles.vip]: isVip },
                  { [styles['secondary__code-currency']]: type === 'secondary' },
                )}
              >
                <img
                  className={cn(
                    styles.currencyIcon,
                    { [styles.currencyIcon_bigMode]: bigCurBlock },
                    { [styles.exchangePage]: isExchangePage },
                    { [styles.repayPage]: isRepaymentPage },
                    { [styles['secondary__code-img']]: type === 'secondary' },
                  )}
                  alt={coin.code}
                  src={coin.logo_url}
                  width={bigCurBlock ? 36 : 20}
                  height={bigCurBlock ? 36 : 20}
                />
                <div className={cn(styles.column)}>
                  {showBalance && <div className={cn(styles.row, styles.available)}>{t('available')}</div>}
                  <div
                    className={cn(
                      styles.balance,
                      styles.balance_desktop,
                      { [styles.balance_bigMode]: bigCurBlock },
                      { [styles.balance_exchange]: isExchangePage },
                      { [styles.repay]: isRepaymentPage },
                      { [styles['secondary__code-balance']]: type === 'secondary' },
                    )}
                  >
                    <p>
                      {showBalance
                        ? coin?.balance && coin.balance > 0
                          ? `${floorNumber(+Number(coin.balance).toFixed(countDecimalPlaces(coin?.balance)), 6)} ${coin.code
                          }`
                          : `0 ${coin.code}`
                        : coin.code}
                    </p>

                    <NetworkLabel
                      className={cn(
                        styles.currencyNetwork,
                        { [styles.currencyNetwork_bigMode]: bigCurBlock },
                        { [styles.repayPage]: isRepaymentPage },
                        { [styles.secondary__network]: type === 'secondary' }
                      )}
                      currency={coin}
                      type={type}
                    />
                  </div>
                  <div
                    className={cn(
                      styles.balance,
                      styles.balance_mobile,
                      { [styles.balance_bigMode]: bigCurBlock },
                      { [styles.balance_exchange]: isExchangePage },
                      { [styles['secondary__code-balance']]: type === 'secondary' },
                    )}
                  >
                    <p>{coin.code}</p>

                    <NetworkLabel
                      className={cn(styles.currencyNetwork,
                        { [styles.currencyNetwork_bigMode]: bigCurBlock },
                        { [styles.secondary__network]: type === 'secondary' }
                      )}
                      currency={coin}
                      type={type}
                    />
                  </div>
                </div>
              </div>
          )}
          {list.length > 0 && isRepaymentPage && (
            <ReactSVG
              src={bigCurBlock ? IconArrow : IconInputArrow}
              className={cn(
                { [styles.openedListArrow]: isVisibleList },
                { [styles.iconArrow]: bigCurBlock },
                { [styles.repayPage]: isRepaymentPage },
              )}
              width={isRepaymentPage ? 24 : 16}
              height={isRepaymentPage ? 24 : 16}
            />
          )}

          {(list.length > 0 && !isRepaymentPage && showList) && (
            <img
              src={bigCurBlock ? IconBigInputArrow : IconInputArrow}
              className={cn(
                { [styles.openedListArrow]: isVisibleList },
                { [styles.iconArrow]: bigCurBlock },
                { [styles.repayPage]: isRepaymentPage },
              )}
            />
          )}
        </div>

        <CoinList
          ref={coinListRef}
          list={list}
          showBalance={showBalance}
          selected={coin}
          onChoose={handleChooseCoin}
          isStableFirst={isStableFirst}
          listFullWidth={listFullWidth}
          isExchangeLayout={isExchangeLayout}
          isVip={isVip}
          isVisibleList={isVisibleList}
          onClose={handleCoinListClose}
          type={type}
        />
      </div>
    </div>
  )
}
